/**
 * All date logic is Asia/Kolkata (IST, UTC+05:30, no DST) — the platform convention (F4.1-s).
 * Never use the device's local timezone for business dates: a phone set to UTC would otherwise
 * put a 02:00 IST delivery on the previous day.
 */

export const IST_TIME_ZONE = 'Asia/Kolkata'
export const IST_OFFSET_MINUTES = 330

type DateInput = Date | string | number

function toDate(value: DateInput): Date {
  const d = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(d.getTime())) throw new RangeError(`Invalid date: ${String(value)}`)
  return d
}

const partsFormatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: IST_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

export interface IstParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

/** Calendar fields of an instant as seen in IST. */
export function istParts(value: DateInput): IstParts {
  const out: Record<string, number> = {}
  for (const p of partsFormatter.formatToParts(toDate(value))) {
    if (p.type !== 'literal') out[p.type] = Number(p.value)
  }
  return {
    year: out.year ?? 0,
    month: out.month ?? 0,
    day: out.day ?? 0,
    hour: out.hour ?? 0,
    minute: out.minute ?? 0,
    second: out.second ?? 0,
  }
}

const pad = (n: number, w = 2) => String(n).padStart(w, '0')

/** "2026-10-18" — the IST business date of an instant. Use as a key for "today". */
export function istDateKey(value: DateInput = Date.now()): string {
  const p = istParts(value)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}`
}

/** "261018" — the YYMMDD part of session / lot codes. */
export function istYYMMDD(value: DateInput = Date.now()): string {
  const p = istParts(value)
  return `${pad(p.year % 100)}${pad(p.month)}${pad(p.day)}`
}

/** The instant of 00:00 IST on the IST date that contains `value`. */
export function startOfIstDay(value: DateInput = Date.now()): Date {
  const p = istParts(value)
  return new Date(Date.UTC(p.year, p.month - 1, p.day) - IST_OFFSET_MINUTES * 60_000)
}

export function isSameIstDay(a: DateInput, b: DateInput): boolean {
  return istDateKey(a) === istDateKey(b)
}

/** Whole IST calendar days from a to b (b later → positive). */
export function istDaysBetween(a: DateInput, b: DateInput): number {
  return Math.round((startOfIstDay(b).getTime() - startOfIstDay(a).getTime()) / 86_400_000)
}

export type Locale = 'hi' | 'en'
const intlLocale = (l: Locale) => (l === 'hi' ? 'hi-IN' : 'en-IN')

/** "18 Oct 2026" / "18 अक्टू॰ 2026". */
export function formatIstDate(value: DateInput, locale: Locale = 'en'): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: IST_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(toDate(value))
}

/** Hindi has no own word for am/pm in Intl ("2:05 pm"), so Hindi times are on the 24-hour clock. */
const clock = (locale: Locale) => (locale === 'hi' ? { hourCycle: 'h23' as const } : {})

/** "18 Oct 2026, 2:05 pm" / "18 अक्टू॰ 2026, 14:05". */
export function formatIstDateTime(value: DateInput, locale: Locale = 'en'): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: IST_TIME_ZONE,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...clock(locale),
  }).format(toDate(value))
}

/** "2:05 pm" / "14:05". */
export function formatIstTime(value: DateInput, locale: Locale = 'en'): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    timeZone: IST_TIME_ZONE,
    hour: 'numeric',
    minute: '2-digit',
    ...clock(locale),
  }).format(toDate(value))
}

/** "5 minutes ago" / "5 मिनट पहले". */
export function formatRelative(
  value: DateInput,
  locale: Locale = 'en',
  now: number = Date.now(),
): string {
  const diffS = Math.round((toDate(value).getTime() - now) / 1000)
  const abs = Math.abs(diffS)
  const rtf = new Intl.RelativeTimeFormat(intlLocale(locale), { numeric: 'auto' })
  if (abs < 45) return rtf.format(0, 'second')
  if (abs < 3600) return rtf.format(Math.round(diffS / 60), 'minute')
  if (abs < 86_400) return rtf.format(Math.round(diffS / 3600), 'hour')
  return rtf.format(Math.round(diffS / 86_400), 'day')
}

/** ISO-8601 with the +05:30 offset, e.g. for `occurred_at` shown to humans in exports. */
export function toIstIso(value: DateInput = Date.now()): string {
  const p = istParts(value)
  const ms = pad(toDate(value).getUTCMilliseconds(), 3)
  return `${p.year}-${pad(p.month)}-${pad(p.day)}T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}.${ms}+05:30`
}
